import { useCallback, useEffect, useRef, useState } from 'react'
import type { HydraulicType } from '@/types/hydraulic'
import {
  createPipeRow,
  dropPromotedDuplicates,
  flattenHydraulicData,
  isDraftId,
} from './tree-data'
import type { HydraulicTreeRow } from './types'
import { CAPACITY_FIELD } from './constants'
import { cellRowId, collectPipeAndDescendants } from './save-rules'
import { toMetricValue, type UnitConverters } from '@/components/grid/unitColumn'

interface UseHydraulicEditsArgs {
  rows: HydraulicType[]
  bulkEditMode: boolean
  authInterfaceId: unknown
  /** Capacity is edited in the user's units and stored in l/m. */
  units: UnitConverters
  updateDraftField: (id: number, field: string, newValue: unknown) => void
}

/**
 * The server rows as a flat tree, plus the bulk-edit buffer laid over them.
 *
 * Edits are held here rather than in the grid's own buffer, so they survive the
 * refetches this page does; `editVersion` is what makes the cells re-read the
 * pending set, since a ref cannot trigger a render.
 */
const useHydraulicEdits = ({
  rows,
  bulkEditMode,
  authInterfaceId,
  units,
  updateDraftField,
}: UseHydraulicEditsArgs) => {
  const editedRowsRef = useRef<Record<string, any>>({})
  // Which specific cells have been changed and are pending save, keyed
  // `${rowId}::${field}`, so those cells can be highlighted amber.
  const editedCellsRef = useRef<Set<string>>(new Set())
  // Bumped whenever an edit lands, so the cells re-run their style function.
  const [editVersion, setEditVersion] = useState(0)
  const bumpEditVersion = useCallback(
    () => setEditVersion((version) => version + 1),
    [],
  )
  /**
   * SCRUM-1032 QC round 7 — moves on a user's edit and nothing else.
   *
   * The autosave used to be armed by `editVersion`, which the save itself
   * bumps to repaint. A failed save therefore scheduled the next one, which
   * failed and scheduled the next: a refused pipe was sent again every few
   * seconds for as long as the step was open. This one only the user moves.
   */
  const [userEditVersion, setUserEditVersion] = useState(0)
  const bumpUserEdit = useCallback(
    () => setUserEditVersion((version) => version + 1),
    [],
  )

  const [gridData, setGridData] = useState<HydraulicTreeRow[]>([])
  const [addPipeRow, setAddPipeRow] = useState<HydraulicTreeRow | null>(null)

  // Build grid data from the server rows. Staged drafts are laid over this in
  // `rowData`, so a refetch rebuilds the tree underneath without losing them.
  useEffect(() => {
    const flattened = flattenHydraulicData(dropPromotedDuplicates(rows))

    // Preserve edited row values across a refetch
    const prevEdited = editedRowsRef.current
    if (bulkEditMode && Object.keys(prevEdited).length > 0) {
      flattened.forEach((row, index) => {
        const edited = prevEdited[`${row.id}`]
        if (edited) flattened[index] = { ...row, ...edited, id: row.id }
      })
    } else {
      editedRowsRef.current = {}
    }

    setGridData(flattened)

    // Shown whenever bulk edit is on (even with no pipes yet) so the first
    // pipe can be added to an empty list.
    const firstRow = rows[0]
    setAddPipeRow(
      bulkEditMode
        ? createPipeRow((firstRow as any)?.interfaceId ?? Number(authInterfaceId))
        : null,
    )
  }, [rows, bulkEditMode, authInterfaceId])

  // Bulk edit: track cell changes. `false` keeps the edit out of the grid's
  // own buffer — the pending set here survives the refetches this page does.
  const handleCellValueChanged = useCallback(
    (event: {
      row: HydraulicTreeRow
      field: string
      oldValue: unknown
      newValue: unknown
    }) => {
      if (!bulkEditMode) return false as const
      if (event.row.isCreateRow) return false as const

      // Capacity is shown and edited in the user's units but stored — and
      // POSTed — in l/m. Returning false below means the grid's own
      // valueSetter never runs, so the conversion has to happen here, before
      // anything is staged.
      const newValue =
        event.field === CAPACITY_FIELD
          ? toMetricValue(
              (event.row as any)[CAPACITY_FIELD],
              event.newValue,
              'l/m',
              units,
            )
          : event.newValue

      if (isDraftId(event.row.id)) {
        updateDraftField(event.row.id, event.field, newValue)
        bumpUserEdit()
        return false as const
      }

      if (event.newValue === event.oldValue) return false as const

      const rowId = `${event.row.id}`
      const patched = { ...event.row, [event.field]: newValue }
      editedRowsRef.current[rowId] = {
        ...editedRowsRef.current[rowId],
        ...patched,
      }
      editedCellsRef.current.add(`${rowId}::${event.field}`)

      setGridData((prev) =>
        prev.map((row) =>
          !row.isCreateRow && row.id === event.row.id
            ? { ...row, [event.field]: newValue }
            : row,
        ),
      )
      bumpEditVersion()
      bumpUserEdit()
      return false as const
    },
    [bulkEditMode, units, updateDraftField, bumpEditVersion, bumpUserEdit],
  )

  /**
   * Round 7 review (H2) — forgets the staged edits of a deleted pipe and of
   * every pipe under it. Rows are read in pages, so "not in the list" cannot
   * stand for "deleted": this is called by the delete itself.
   */
  const forgetPipeEdits = useCallback(
    (id: number): string[] => {
      const gone = collectPipeAndDescendants(id, [
        ...gridData,
        ...Object.values(editedRowsRef.current),
      ])
      gone.forEach((rowId) => delete editedRowsRef.current[rowId])
      editedCellsRef.current = new Set(
        [...editedCellsRef.current].filter(
          (key) => !gone.has(cellRowId(key)),
        ),
      )
      bumpEditVersion()
      return [...gone]
    },
    [gridData, editedRowsRef, editedCellsRef, bumpEditVersion],
  )

  return {
    gridData,
    setGridData,
    addPipeRow,
    editedRowsRef,
    editedCellsRef,
    editVersion,
    bumpEditVersion,
    userEditVersion,
    handleCellValueChanged,
    forgetPipeEdits,
  }
}

export default useHydraulicEdits
